import { z } from "zod";
import { evCommandSourceSchema, evCommandStatusSchema, evCommandTypeSchema, evConnectionCloseReasonSchema, evConnectorStatusSchema, evIdentificationSchema, evLimitReasonSchema, evPaymentMethodSchema, evPaymentStatusSchema, evSessionPhaseSchema, } from "../ev/enums.js";
import { defineEvent } from "./registry.js";
/**
 * Şarj olayları (Prompt 19, ADR 0019 §19). Yük KİŞİSEL VERİ TAŞIMAZ: idTag / plaka / ad YOK — yalnızca kimlikler, durumlar,
 * kWh / tutar dizgeleri. Zarf `metadata.stationId` taşır → istasyon odası (canlı pano, kasa).
 */
const money = z.string().regex(/^-?\d{1,19}$/);
const kwh = z.string().regex(/^\d{1,15}\.\d{3}$/);
const at = z.iso.datetime({ offset: true });
const base = { companyId: z.uuid(), stationId: z.uuid() };
const charger = { ...base, chargerId: z.uuid() };
export const evChargerConnectedV1 = defineEvent({
    type: "ev.charger.connected",
    version: 1,
    description: "Şarj ünitesi OCPP sunucusuna bağlandı",
    realtime: ["station"],
    payload: z.object({ ...charger, at }),
});
export const evChargerDisconnectedV1 = defineEvent({
    type: "ev.charger.disconnected",
    version: 1,
    description: "Şarj ünitesinin bağlantısı koptu (erişilemezlik başladı)",
    realtime: ["tenant", "station"],
    payload: z.object({ ...charger, at, reason: evConnectionCloseReasonSchema }),
});
export const evChargerBootedV1 = defineEvent({
    type: "ev.charger.booted",
    version: 1,
    description: "Şarj ünitesi açıldı (BootNotification: üretici / model / yazılım)",
    realtime: ["station"],
    payload: z.object({
        ...charger,
        at,
        vendor: z.string().nullable(),
        model: z.string().nullable(),
        firmwareVersion: z.string().nullable(),
    }),
});
export const evConnectorStatusChangedV1 = defineEvent({
    type: "ev.connector.status_changed",
    version: 1,
    description: "Konnektör durumu değişti (0 = ünitenin kendisi)",
    realtime: ["station"],
    payload: z.object({
        ...charger,
        connectorNo: z.number().int(),
        status: evConnectorStatusSchema,
        errorCode: z.string().nullable(),
        at,
    }),
});
export const evChargerFaultStartedV1 = defineEvent({
    type: "ev.charger.fault_started",
    version: 1,
    description: "Şarj ünitesi / konnektör arızaya geçti",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...charger,
        connectorNo: z.number().int(),
        errorCode: z.string().nullable(),
        at,
    }),
});
export const evChargerFaultClearedV1 = defineEvent({
    type: "ev.charger.fault_cleared",
    version: 1,
    description: "Şarj ünitesinin / konnektörün arızası giderildi (süre saniye)",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...charger,
        connectorNo: z.number().int(),
        errorCode: z.string().nullable(),
        durationSeconds: z.number().int(),
    }),
});
const session = {
    ...charger,
    sessionId: z.uuid(),
    connectorNo: z.number().int().nullable(),
};
export const evSessionStartedV1 = defineEvent({
    type: "ev.session.started",
    version: 1,
    description: "Şarj oturumu başladı",
    realtime: ["station"],
    payload: z.object({
        ...session,
        identification: evIdentificationSchema,
        customerProfileId: z.uuid().nullable(),
        startedAt: at,
    }),
});
export const evSessionUpdatedV1 = defineEvent({
    type: "ev.session.updated",
    version: 1,
    description: "Şarj oturumu ölçümü / evresi güncellendi (sınırlı sıklıkta: evre değişiminde ya da en çok dakikada bir)",
    realtime: ["station"],
    payload: z.object({
        ...session,
        phase: evSessionPhaseSchema,
        energyKwh: kwh,
        powerW: z.number().int().nullable(),
        socPercent: z.string().nullable(),
    }),
});
export const evSessionCompletedV1 = defineEvent({
    type: "ev.session.completed",
    version: 1,
    description: "Şarj oturumu tamamlandı (araç çekildi) — tutar kesinleşti",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...session,
        energyKwh: kwh,
        chargingSeconds: z.number().int(),
        idleSeconds: z.number().int(),
        totalAmount: money,
        paymentStatus: evPaymentStatusSchema,
        recovered: z.boolean(),
    }),
});
export const evSessionPaidV1 = defineEvent({
    type: "ev.session.paid",
    version: 1,
    description: "Şarj oturumunun ödeme durumu kesinleşti (kasa, fatura, abonelik ya da ücretsiz)",
    realtime: ["station"],
    payload: z.object({
        ...session,
        paymentStatus: evPaymentStatusSchema,
        paymentMethod: evPaymentMethodSchema,
        totalAmount: money,
        posSaleId: z.uuid().nullable(),
        invoiceId: z.uuid().nullable(),
    }),
});
export const evSessionDoublePaymentV1 = defineEvent({
    type: "ev.session.double_payment",
    version: 1,
    description: "Şarj oturumu ikinci kez ödendi (çevrimdışı çift ödeme) — iade / düzeltme gerekir",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...session,
        posSaleId: z.uuid(),
        amount: money,
    }),
});
export const evSessionPaymentRevertedV1 = defineEvent({
    type: "ev.session.payment_reverted",
    version: 1,
    description: "Oturumu ödeyen kasa satışı tamamen iptal edildi — oturum yeniden ödenmedi (kasada tekrar sunulur)",
    realtime: ["tenant", "station"],
    payload: z.object({
        ...session,
        posSaleId: z.uuid(),
        voidSaleId: z.uuid().nullable(),
        totalAmount: money,
    }),
});
export const evCommandFinishedV1 = defineEvent({
    type: "ev.command.finished",
    version: 1,
    description: "Şarj ünitesine gönderilen komut sonuçlandı",
    realtime: ["station"],
    payload: z.object({
        ...charger,
        commandId: z.uuid(),
        commandType: evCommandTypeSchema,
        source: evCommandSourceSchema,
        status: evCommandStatusSchema,
        sessionId: z.uuid().nullable(),
    }),
});
export const evLoadRebalancedV1 = defineEvent({
    type: "ev.load.rebalanced",
    version: 1,
    description: "İstasyonun şarj gücü oturumlara yeniden paylaştırıldı",
    realtime: ["station"],
    payload: z.object({
        ...base,
        reason: evLimitReasonSchema,
        availableW: z.number().int(),
        limits: z.array(z.object({ sessionId: z.uuid(), limitW: z.number().int(), suspended: z.boolean(), sent: z.boolean() })),
    }),
});
export const evPartnerImportCompletedV1 = defineEvent({
    type: "ev.partner.import_completed",
    version: 1,
    description: "Şarj ağı işletmecisinden oturum içe aktarımı tamamlandı",
    realtime: ["tenant"],
    payload: z.object({
        ...base,
        importId: z.uuid(),
        importedCount: z.number().int(),
        skippedCount: z.number().int(),
        totalAmount: money,
    }),
});
//# sourceMappingURL=ev.v1.js.map